import { inject, Injectable, signal } from '@angular/core';
import { SocketService } from '@core/services/socket.service';
import {
  King,
  KingHistory,
  KingHit,
  KingUser,
} from './interfaces/king.interface';
import { Skin } from '@core/interfaces/skin.interface';
import { sleep } from '@core/utils/sleep.util';
import { KingType } from './types/king.type';
import { ModalService } from '@features/modals/services/modal.service';
import { SessionService } from '@core/services/session.service';

/**
 * Central game state and socket management for King mode.
 *
 * Animation Architecture — 3 coordinated queues:
 *
 * 1. KingNewService (this file) — HP state queue
 *    Receives socket 'king_attack' events, queues hits, and updates game HP
 *    after a delay synced to GUN_ANIMATION_DURATION.
 *
 * 2. KingSideTopComponent — Weapon animation queue
 *    Watches `hits` signal for new entries where `hit.by === role`.
 *    Plays weapon sprite animation for GUN_ANIMATION_DURATION before dequeuing.
 *
 * 3. KingUserAvatarComponent — Damage display queue
 *    Watches `hits` signal for new entries where `hit.by !== role` (i.e. this user is the target).
 *    Shows damage arc, HP bar animation, and hit indicators (critical/miss/damage).
 *
 * All three queues use `lastProcessedHitIndex` to independently track which hits
 * they've consumed from the shared `hits` signal. Timing is coordinated via
 * GUN_ANIMATION_DURATION so weapon visuals, HP updates, and damage numbers
 * appear in sync.
 */
@Injectable({
  providedIn: 'root',
})
export class KingNewService {
  private readonly socketService = inject(SocketService);
  private readonly modalService = inject(ModalService);
  private readonly sessionService = inject(SessionService);

  public readonly GUN_ANIMATION_DURATION = 4000;

  readonly maxDeposit = 5000;
  readonly minDeposit = 2;

  timer = signal(-1);
  private timerEndTime: number | null = null;
  timerTotal = signal(0);

  queue = signal<KingUser[]>([]);

  game = signal<King | null>(null);
  hits = signal<KingHit[]>([]);

  history = signal<KingHistory[]>([]);

  // Hit processing queue to prevent race conditions
  private hitQueue: KingHit[] = [];
  private isProcessingHits = false;

  constructor() {
    this.initKingService();
    setInterval(() => {
      if (this.timerEndTime !== null && this.game()?.status === 'part_waiting') {
        const remaining = Math.ceil((this.timerEndTime - Date.now()) / 1000);
        this.timer.set(remaining > 0 ? remaining : 0);
      }
    }, 250);
  }

  initKingService() {
    this.socketService.socket.on(
      'king_queue',
      (data: { queue: KingUser[] }) => {
        this.queue.set(data.queue);
      }
    );

    this.socketService.socket.on('king_queue_add', (data: KingUser) => {
      this.queue.update((prev) => [...prev, data]);
    });

    this.socketService.socket.on('king_queue_remove', (data: number) => {
      this.queue.update((prev) => prev.filter((queue) => queue?.id !== data));
    });

    this.socketService.socket.on('king_update', (data) => {
      const kingFixed = this.fixUser(data.king);
      if (kingFixed) {
        this.game.update((prev) => ({ ...prev, king: kingFixed }));

        if (this.sessionService.profile()?.steamid === kingFixed.steamid) {
          this.modalService.closeModals(['TradeModal']);
        }
      }
    });

    this.socketService.socket.on('part_update', (data) => {
      const partFixed = this.fixUser(data.part);
      if (partFixed) {
        this.game.update((prev) => ({ ...prev, part: partFixed }));

        if (this.sessionService.profile()?.steamid === partFixed.steamid) {
          this.modalService.closeModals(['TradeModal']);
        }
      }
    });

    this.socketService.socket.on('king_initiate', (data) => {
      // Clear hits and hit queue for new game
      this.hits.set([]);
      this.clearHitQueue();

      this.game.set({
        ...data,
        king: this.fixUser(data.king),
        part: this.fixUser(data.part),
      });
      if (data.time === -1) {
        this.timerEndTime = null;
        this.timerTotal.set(0);
        this.timer.set(-1);
      } else {
        const remaining = data.time - data.timenow;
        this.timerTotal.set(remaining);
        this.timerEndTime = Date.now() + Math.max(remaining, 0) * 1000;
        this.timer.set(remaining > 0 ? remaining : 0);
      }
    });

    this.socketService.socket.on('show_king_history', (data) => {
      this.history.set(
        data.history
          .sort((a: King, b: King) => {
            if (a.gameid === b.gameid) {
              return (b.roundid ?? 0) - (a.roundid ?? 0);
            }

            return (b.gameid ?? 0) - (a.gameid ?? 0);
          })
          .map((game: KingHistory) => {
            return {
              ...game,
              king: this.fixUser(game.king),
              part: this.fixUser(game.part),
            };
          })
      );
    });

    this.socketService.socket.on('king_attack', (hit: KingHit) => {
      this.queueHit(hit);
    });

    this.preloadImages();
  }

  /**
   * Queue a hit for sequential processing.
   * This prevents race conditions when multiple hits arrive quickly.
   */
  private queueHit(hit: KingHit): void {
    // Get current game state for initial calculations
    const game = this.game();
    hit.toUser = hit.by === 'king' ? game?.part : game?.king;
    hit.damage = (hit.damage / 100) * (hit.toUser?.initial_hp ?? 0);

    // Add hit to signals immediately for UI reactivity
    this.hits.update((prev) => [...prev, hit]);

    // Queue for sequential HP processing
    this.hitQueue.push(hit);

    if (!this.isProcessingHits) {
      this.processNextHit();
    }
  }

  /**
   * Process hits sequentially to maintain sync between animations and HP updates.
   */
  private async processNextHit(): Promise<void> {
    if (this.hitQueue.length === 0) {
      this.isProcessingHits = false;
      return;
    }

    this.isProcessingHits = true;
    const hit = this.hitQueue.shift()!;

    // Wait for weapon animation to reach the "hit" moment
    await sleep(this.GUN_ANIMATION_DURATION - 2000);

    // Get FRESH game reference after the wait (not stale)
    const currentGame = this.game();
    if (!currentGame) {
      this.processNextHit();
      return;
    }

    // Calculate HP based on the hit's hp_now percentage
    const targetUser = hit.by === 'king' ? currentGame.part : currentGame.king;
    if (!targetUser) {
      this.processNextHit();
      return;
    }

    const hpAfter = (targetUser.initial_hp ?? 0) * ((hit.hp_now ?? 0) / 100);

    // Update the correct user's HP
    if (hit.by === 'king') {
      this.game.update((prev) => ({
        ...prev,
        part: prev?.part ? { ...prev.part, hp: hpAfter } : prev?.part,
      }));
    } else {
      this.game.update((prev) => ({
        ...prev,
        king: prev?.king ? { ...prev.king, hp: hpAfter } : prev?.king,
      }));
    }

    // Process next hit in queue
    this.processNextHit();
  }

  /**
   * Clear hit queue when a new game starts.
   */
  clearHitQueue(): void {
    this.hitQueue = [];
    this.isProcessingHits = false;
  }

  /**
   * Reset only animation-related state, not game data.
   * Game data (game, queue, history) is kept because socket events maintain it.
   * Called when entering/leaving King page to ensure clean animation state.
   */
  resetAnimationState(): void {
    // Only clear hits and hit queue - these affect animations
    this.hits.set([]);
    this.clearHitQueue();
  }

  /**
   * Full reset of all game state.
   * Only used internally when king_initiate is received.
   */
  resetGameState(): void {
    this.game.set(null);
    this.hits.set([]);
    this.queue.set([]);
    this.timerEndTime = null;
    this.timerTotal.set(0);
    this.timer.set(-1);
    this.clearHitQueue();
  }

  private preloadImages() {
    const images = [
      '/assets/images/king/big-kill.png',
      '/assets/images/king/enforcer.png',
      '/assets/images/king/family-business.png',
      '/assets/images/king/golden-pan.png',
      '/assets/images/king/kukri.png',
      '/assets/images/king/lugermorph.png',
      '/assets/images/king/minigun.png',
      '/assets/images/king/panic-attack.png',
      '/assets/images/king/rocket-launcher.png',
      '/assets/images/king/saxxy.png',
      '/assets/images/king/scattergun.png',
      '/assets/images/king/shotgun.png',
      '/assets/images/king/ak-hitmarker.png',
      '/assets/images/king/pistol-hitmarker.png',
      '/assets/images/king/rock-hitmarker.png',
      '/assets/images/king/sniper-hitmarker.png',
    ];
    for (const image of images) {
      const img = new Image();
      img.src = image;
    }
  }

  async joinKing(
    items: Skin[],
    type: KingType
  ): Promise<{ offerid: string; hash: string }> {
    return await this.socketService.emit('king_deposit', {
      skins: items,
      type: typeof type === 'number' ? type : type === 'fight' ? 0 : 1,
    });
  }

  /**
   * Request the current game state from the server.
   * Called when navigating to King New to ensure we have the latest state.
   */
  requestGameState(): void {
    this.socketService.socket.emit('king_join');
  }

  private fixUser(user?: KingUser) {
    if (user?.steamid?.toString() === '-1') {
      console.warn('[KingNewService] fixUser: received user with steamid "-1", treating as empty slot');
      user = undefined;
    }

    if (user) {
      // Only fix if not already fixed (prevents double-calculation)
      if (!user.initial_hp) {
        const initialHP = user.value * 1000;
        user.hp = initialHP * ((user.hp ?? 100) / 100);
        user.initial_hp = initialHP;
      }
    }

    return user;
  }
}
